// The `evidence/leaderboard` referral-link WRITE — carved from
// `src/hooks/mutation/useMutateEvidenceReferralLink.ts` (now a bodied delegate).
//
// TWO LEGS COULD NOT CROSS AND BOTH ARE PORTS, which is what keeps this a T2
// rather than the T1 `meta` tier:
//
//   * `invalidateCurrentUser()` (severance cohort S6) is WORKSPACE's command and
//     is COMPOSITE — it invalidates `GET_USER` *and* the commerce `GET_PRODUCTS`
//     behind one try/catch — so re-implementing its workspace leg here would
//     silently drop a commerce invalidation. Per the delivery command rule the
//     owning domain exports the command and app integration sequences it; it is
//     deliberately NOT injected through `configureEvidenceData`, which would
//     hide an edge the manifest cannot see. This is the same arrangement
//     `packages/workspace`'s own `useMutateUpdateUser` records for the same
//     command.
//   * THE ERROR TOAST REPLACES ITS DESCRIPTION RATHER THAN APPENDING TO IT.
//     The delegate renders `get(error.response, 'data.detail.msg', <fallback>)`
//     — the backend detail INSTEAD OF the fallback copy, not after it — and
//     `appendBackendDetail` on a `meta` toast concatenates. Substitution and
//     concatenation are different strings on the same failure, so the copy stays
//     app-side with the i18next lookup that produces it.
//
// The error type is preserved as `AxiosError` rather than narrowed: it is the
// public shape every consumer of this mutation already sees, and the delegate's
// `error.response` read depends on it.
import { useMutation } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  GENERATE_EVIDENCE_REFERRAL_LINK,
  type EvidenceReferralLinkResponse,
} from '../../contracts';
import { evidenceLeaderboardApi } from '../api/evidence-leaderboard';

export interface UseMutateEvidenceReferralLinkOptions {
  /**
   * App-reaction port: fired where `invalidateCurrentUser()` fired, in
   * `onSuccess`, and AWAITED exactly as it was — the referral link is written
   * onto the user record, so a caller that reads the user immediately after the
   * mutation settles must see the refreshed one.
   */
  onReferralLinkGenerated?: () => Promise<void> | void;
  /**
   * App-reaction port: fired where the destructive toast fired, in `onError`,
   * with the error unchanged so the delegate can read `response.data.detail.msg`
   * off it.
   */
  onGenerateReferralLinkError?: (error: AxiosError) => void;
}

/**
 * Hook to generate or retrieve the user's Evidence referral link.
 *
 * @returns Mutation result with the referral link
 */
export function useMutateEvidenceReferralLink({
  onReferralLinkGenerated,
  onGenerateReferralLinkError,
}: UseMutateEvidenceReferralLinkOptions = {}) {
  return useMutation<EvidenceReferralLinkResponse, AxiosError>({
    mutationKey: [GENERATE_EVIDENCE_REFERRAL_LINK],
    mutationFn: async () => {
      return evidenceLeaderboardApi.generateEvidenceReferralLink();
    },
    onSuccess: async () => {
      await onReferralLinkGenerated?.();
    },
    onError: (error) => {
      onGenerateReferralLinkError?.(error);
    },
  });
}
